<template>
    <div class="object-listing-component my-12 mx-auto container" ref="dataset">
        <localized-dataset
            v-slot="{ ds }"
            :ds-data="items"
            :ds-filter-fields="filterFields"
            :ds-sortby="[selectedSort]"
            @update:ds-data="onUpdate"
        >
            <TransitionRoot v-if="filterEnabled" as="template" :show="mobileFiltersOpen">
                <DialogComponent class="relative z-40 xl:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild
                        as="template"
                        enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <div class="fixed inset-0 bg-black/25"/>
                    </TransitionChild>
                    <div class="fixed inset-0 z-40 flex">
                        <TransitionChild
                            as="template"
                            enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                        >
                            <DialogPanel
                                class="relative ml-auto flex size-full max-w-full flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl"
                            >
                                <div class="flex items-center justify-between px-4">
                                    <h2 class="text-lg font-medium text-gray-900">{{ i18n.filters }}</h2>
                                    <button
                                        type="button"
                                        class="-mr-2 flex size-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                                        @click="mobileFiltersOpen = false"
                                    >
                                        <span class="sr-only">{{ i18n.closeMenu }}</span>
                                        <XMarkIcon class="size-6" aria-hidden="true"/>
                                    </button>
                                </div>
                                <DatasetFilter
                                    :filters="filters"
                                    :i18n="i18n"
                                    :form-class="'py-8 px-8 md:px-16 border-t border-gray-200'"
                                    @update:filter-fields="updateFields"
                                    @update:filter-apply="applyFilter"
                                />
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </DialogComponent>
            </TransitionRoot>
            <main>
                <div v-if="searchEnabled" class="grid grid-cols-1 gap-8 pb-8 xl:grid-cols-4">
                    <div class="hidden xl:block"/>
                    <div :class="[filterEnabled ? 'lg:col-span-2 xl:col-span-3' : 'lg:col-span-3 xl:col-span-4']">
                        <DatasetSearch
                            :ds-search-placeholder="i18n.search"
                            class="w-full px-4 py-2 bg-sand-10 border border-anthrazit-40 gap-2"
                        />
                    </div>
                </div>
                <div :class="['grid xl:hidden mb-8 xl:grid-cols-4', showSorting ? 'grid-cols-2 gap-8':'grid-cols-1 gap-8']">
                    <div v-if="showSorting" class="flex justify-end items-start">
                        <DatasetSorting
                            :sorting="sorting"
                            :element="'button'"
                            :container-class="'relative z-10 button-sm inverted border-2 border-sand-60'"
                            :i18n="i18n"
                            :default-sorting="defaultSorting"
                            :show-selected-option="false"
                            @update:sorting="updateSorting"/>
                    </div>
                    <div :class="['flex', showSorting ? 'justify-start items-start':'justify-center items-center']">
                        <button
                            v-if="filterEnabled"
                            type="button"
                            class="button-sm bg-sand-40 hover:bg-sand-40 text-black/95"
                            @click.prevent="mobileFiltersOpen = true"
                        >
                            <SvgIcon name="more-filters"/>
                            <label>{{ i18n.moreFilters }}</label>
                        </button>
                    </div>
                </div>
                <div :class="['grid gap-8 grid-cols-1', filterEnabled ? 'lg:grid-cols-3 xl:grid-cols-4' : 'lg:grid-cols-2 xl:grid-cols-3']">
                    <div v-if="filterEnabled" class="hidden xl:block h-8 text-anthrazit-90 text-2xl font-bold font-sans leading-2">{{ i18n.filters }}</div>
                    <div class="text-left" :data-page-count="ds.dsPagecount">
                        <DatasetInfo v-if="ds.dsResultsNumber > itemsPerPage"/>
                        <DatasetShow :ds-show-entries="itemsPerPage" class="hidden"/>
                    </div>
                    <div :class="['hidden', sortingEnabled ? 'xl:block':'xl:block']"/>
                    <div v-if="sortingEnabled" class="text-right hidden xl:block">
                        <DatasetSorting
                            :sorting="sorting"
                            :i18n="i18n"
                            :default-sorting="defaultSorting"
                            @update:sorting="updateSorting"/>
                    </div>
                </div>
                <section aria-labelledby="products-heading" class="py-6">
                    <div class="grid grid-cols-1 gap-8 lg:gap-x-0 xl:gap-8 xl:grid-cols-4">
                        <DatasetFilter
                            v-if="filterEnabled"
                            :filters="filters"
                            :i18n="i18n"
                            @update:filter-fields="updateFields"
                        />
                        <div :class="['items-container', filterEnabled ? 'lg:col-span-2 xl:col-span-3' : 'lg:col-span-3 xl:col-span-4']">
                            <DatasetItem :class="gridClass">
                                <template #default="{ rowIndex }">
                                    <slot :name="'item_' + rowIndex"/>
                                </template>
                            </DatasetItem>
                            <div
                                v-if="displayPager"
                                class="d-flex flex-md-row flex-column justify-content-between align-items-center">
                                <DatasetPager @update:page="updatePage"/>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </localized-dataset>
    </div>
</template>
<script>
import {
    Dataset,
    DatasetItem,
    DatasetShow,
    DatasetInfo,
    DatasetSearch,
} from "vue-dataset"
import { useTemplateRef } from 'vue'
import {
    Dialog as DialogComponent,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue"

import DatasetFilter from "@components/ObjectListing/DatasetFilter.vue"
import DatasetSorting from "@components/ObjectListing/DatasetSorting.vue"
import DatasetPager from "@components/ObjectListing/DatasetPager.vue"
import {initFlowbite} from "flowbite"
import {XMarkIcon} from "@heroicons/vue/24/outline/index.js"
import SvgIcon from "@components/SvgIcon.vue"

let translation = {}

export default {
    name: "ObjectListing",
    components: {
        LocalizedDataset: {
            ...Dataset,
            provide() {
                return {
                    datasetI18n: translation
                }
            },
        },
        DialogComponent,
        DialogPanel,
        DatasetSorting,
        TransitionChild,
        TransitionRoot,
        XMarkIcon,
        DatasetItem,
        DatasetInfo,
        DatasetShow,
        DatasetSearch,
        DatasetPager,
        DatasetFilter,
        SvgIcon,
    },
    props: {
        i18n: {
            type: Object,
            required: true,
            default: () => ({
                show: "Show",
                entries: "entries",
                previous: "Previous",
                next: "Next",
                showing: "Showing",
                showingTo: "to",
                showingOf: "of",
                showingEntries: "entries",
                reset: "Reset",
                search: "Search",
                apply: "Apply",
                showMore: "View more",
                moreFilters: "More Filters",
                closeMenu: "Close menu",
                filters: "Filters",
                sorting: "Sorting",
            }),
        },
        items: {
            type: Array,
            required: true,
        },
        sorting: {
            type: Array,
            required: true,
        },
        defaultSorting: {
            type: Object,
            required: true,
        },
        filters: {
            type: Array,
            required: true,
        },
        filterEnabled: {
            type: Boolean,
            default: true,
        },
        searchEnabled: {
            type: Boolean,
            default: true,
        },
        sortingEnabled: {
            type: Boolean,
            default: true,
        },
        gridClass: {
            type: String,
            default: "grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        },
        itemsPerPage: {
            type: Number,
            default: 10,
        },
    },
    data: function () {
        return {
            filtered: [],
            selectedSort: "",
            filterFields: {},
            mobileFiltersOpen: false,
            container: useTemplateRef('dataset')
        }
    },

    computed: {
        displayPager() {
            return this.filtered.length > this.itemsPerPage
        },

        showSorting() {
            return this.sortingEnabled && this.sorting.length > 1
        },
    },

    mounted() {
        initFlowbite()
    },

    created() {
        translation = this.i18n
        this.filtered = this.clone(this.items)
    },

    methods: {
        updateFields(fields) {
            this.filterFields = fields
        },

        applyFilter(fields) {
            this.filterFields = fields
            this.mobileFiltersOpen = false
        },

        clone(obj) {
            return JSON.parse(JSON.stringify(obj || {}))
        },

        onUpdate(data) {
            if (data.length !== this.filtered.length) {
                this.filtered = data
            }
        },

        updateSorting(selectedSort) {
            this.selectedSort = selectedSort
        },
        updatePage(page) {
            this.container.scrollIntoView({ behavior: 'smooth'});
        }
    },
}
</script>
<style scoped lang="scss">
</style>
